import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import classNames from 'classnames';
import Cookies from 'universal-cookie';
import { useConfigStore, useAppStore, useAuthStore } from '../../stores';
import { removeYtCookies } from './PersonalizationHelpers';
import { joinNames } from '../../utils/common';

import Modal from '../Modal/Modal';
import PersonalizationForm from './Form/FormComponents/PersonalizationForm';
import SystemMessage from '../SystemMessage/SystemMessage';
import { useFavoriteButtons } from './Favorite/FavoriteButtonsContext';
import './Personalization.scss';

const cookies = new Cookies();

const Personalization = ({ salespersonCrmId }) => {
  const [formModalSize, setFormModalSize] = useState('small');
  const [formModalIsOpen, setFormModalIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [hasCRMResults, setHasCRMResults] = useState(false);
  const [cookieDataDump, setCookieDataDump] = useState(null);
  const [hasPersonalization, setHasPersonalization] = useState(false);
  const [showPersonalization, setShowPersonalization] = useState(false);
  const [isUpdatingProspect, setIsUpdatingProspect] = useState(false);

  // Zustand stores
  const config = useConfigStore((state) => state.config);
  const crmConfig = useConfigStore((state) => state.crmConfig);
  const personalizationActive = useAppStore((state) => state.personalizationActive);
  const setPersonalizationActive = useAppStore((state) => state.setPersonalizationActive);
  const settings = useAppStore((state) => state.settings);
  const isLoggedIn = useAuthStore((state) => state.isLoggedIn);
  const salespersonNid = useAuthStore((state) => state.salespersonNid);
  const { syncFavoritesFromCookies } = useFavoriteButtons();

  const { machineName } = crmConfig || {};
  const { isKiosk } = settings || {};
  const { features } = config || {};

  const resident = cookies.get('resident');
  const names = joinNames(
    resident?.first_name,
    resident?.last_name,
    resident?.person2_first_name,
    resident?.person2_last_name,
    null,
    ' and ',
  );

  const statusMessage = personalizationActive && resident && (
    <>
      {'Personalized for '}
      <b>{names}</b>
    </>
  );
  const adminWarning = salespersonNid === 1 && 'User is admin - session will not be recorded';
  const sherpaWarning = machineName === 'sherpa' && !salespersonCrmId && (
    <>
      CRM info missing - session will not be recorded. Contact
      {' '}
      <a href="mailto:support@thevectre.com">support@thevectre.com</a>
    </>
  );

  // Do we show personalization?
  useEffect(() => {
    if (!isKiosk
      && hasPersonalization
      && isLoggedIn) {
      setShowPersonalization(true);
    } else {
      setShowPersonalization(false);
    }
  }, [isKiosk, hasPersonalization, isLoggedIn]);

  // Set personalizaiton access based on community config. community crm
  // creds and user crm creds.
  useEffect(() => {
    let rawHasPersonalization = false;
    // Does the community allow personalization?
    if (features) {
      rawHasPersonalization = features?.includes('personalization');
      // Sherpa requires them.
      // Enquire doesn't care.
      // Welcome Home doesn't require user creds, but will use them if they are available;
      if (machineName === 'sherpa' && !salespersonCrmId) {
        rawHasPersonalization = false;
      }
    }
    setHasPersonalization(rawHasPersonalization);
  }, [salespersonCrmId, features, machineName]);

  /**
   * Begins a personalization session by updating state variables.
   */
  const beginSession = () => {
    setPersonalizationActive(true);
  };

  /**
   * On page load, checks for an active session for personalization.
   *
   * Sets the state to active when there's a session cookie present.
   */
  useEffect(() => {
    const residentSession = cookies.get('resident');
    if (residentSession) {
      beginSession();
    }
  }, []);

  // On load, listen for window or tab close/refresh and warn user.
  useEffect(() => {
    // If we're using a CRM, warn the user on tab close because CRM changes
    // aren't sent until "End Personalization" is clicked.
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };

    if (machineName !== 'drupal' && personalizationActive) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [machineName, personalizationActive]);

  useEffect(() => {
    if (currentStep === 4) {
      setFormModalSize('small');
    }
    if (currentStep === 'endPersonalizationWarning') {
      setFormModalSize('small');
    }
  }, [currentStep]);

  /**
   * Go back a step the personalization process.
   *
   * If we're on step 2 going to 1, updated the modal size as well.
   */
  const goBackStep = () => {
    let goToPage;
    if (currentStep === 2) {
      setFormModalSize('small');
      goToPage = 1;
    }

    // If we're on the 'Not found' step, go back to step 1.
    if (currentStep === 3) {
      setFormModalSize('small');
      goToPage = 1;
    }

    if (currentStep === 4) {
      // If we're on the 'Edit Info/Create new' step, go back to step 2. If
      // there were results found in step 2, go to page 2. If there were not, go
      // to page 1.
      goToPage = hasCRMResults ? 2 : 1;
      if (!hasCRMResults) {
        setFormModalSize('small');
      }
    }

    setCurrentStep(goToPage);
  };

  /**
   * Cancel personalization and reset state variables.
   *
   * Removes any cookies that we have set.
   */
  const cancelPersonalization = () => {
    setFormModalSize('small');
    setFormModalIsOpen(false);
    setPersonalizationActive(false);
    setHasCRMResults(false);
    removeYtCookies(cookies);
    syncFavoritesFromCookies();
  };

  /**
   * Close modal by resetting state variables.
   */
  const closeModal = () => {
    setFormModalIsOpen(false);
    setFormModalSize('small');
  };

  /**
   * Start/Stop the personalization process.
   *
   * Also present a modal to the user when cancelling the process to prevent
   * accidental interruption of a session.
   */
  const togglePersonalizationProcess = () => {
    // If personalization is currently active, warn the user
    if (personalizationActive) {
      setCurrentStep('endPersonalizationWarning');
    } else {
      // Else, begin the personalization process.
      setCurrentStep(1);
    }
    setFormModalIsOpen(true);
    setFormModalSize('small');
  };

  return showPersonalization && (
    <>
      <Modal
        isOpen={formModalIsOpen}
        modalSize={formModalSize}
        modalClassName="modal--personalization"
        handleModalHide={() => closeModal()}
        showBlur
      >
        <PersonalizationForm
          updateModalSize={setFormModalSize}
          beginSession={beginSession}
          closeModal={closeModal}
          currentStep={currentStep}
          setCurrentStep={setCurrentStep}
          setHasCRMResults={setHasCRMResults}
          cookieDataDump={cookieDataDump}
          setCookieDataDump={setCookieDataDump}
          setPersonalizationActive={setPersonalizationActive}
          isUpdatingProspect={isUpdatingProspect}
          setIsUpdatingProspect={setIsUpdatingProspect}
          goBackStep={goBackStep}
          cancelPersonalization={cancelPersonalization}
        />
      </Modal>

      <SystemMessage
        status="info"
        message={statusMessage || null}
        showIcon={false}
        className="personalization__status"
        testId="personalization-status"
        fading
      />

      <SystemMessage
        status="warning"
        message={adminWarning || null}
        testId="personalization-admin-warning"
        fading
      />

      <SystemMessage
        status="warning"
        message={sherpaWarning || null}
        testId="personalization-crm-warning"
        fading
      />

      <button
        className={classNames('personalization__toggle', {
          'personalization__toggle--active': personalizationActive,
        })}
        onClick={() => togglePersonalizationProcess()}
        type="button"
        data-testid="personalization-toggle"
      >
        {personalizationActive
          ? 'Stop Personalization'
          : 'Start Personalization'}
      </button>
    </>
  );
};

Personalization.propTypes = {
  salespersonCrmId: PropTypes.string,
};

Personalization.defaultProps = {
  salespersonCrmId: null,
};

export default Personalization;
